import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Elenwear cart drawer cross-sell row.
 *
 * Verified against this theme's actual cart update system before wiring in:
 * this Horizon build dispatches/listens for `CartLinesUpdateEvent` from
 * `@shopify/events` (see assets/product-form.js, assets/component-cart-items.js),
 * not a generic `cart:update` CustomEvent — an earlier draft of this file
 * assumed the latter and would have silently never refreshed the header cart
 * bubble or been noticed by Horizon's own cart components. Listeners read the
 * resolved value off `event.promise`, not `event.detail`.
 */
if (!customElements.get('ew-cart-upsell')) {
  customElements.define(
    'ew-cart-upsell',
    class extends HTMLElement {
      connectedCallback() {
        this.base = (this.dataset.root || '/').replace(/\/$/, '');
        this.requestId = 0;
        this.addEventListener('click', this.onClick);
        // Listened for on document, not on self: the event is dispatched by
        // other components (line item quantity, remove, other add-to-cart
        // controls) that are not inside this element.
        document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
        this.load();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
      }

      onCartLinesUpdate = (event) => {
        if (event.target === this) return;

        event.promise
          ?.then(async ({ cart }) => {
            const items = cart?.items || [];
            if (!items.length) return;

            // The element is excluded from morphing, so the server no longer
            // refreshes `data-product-id`. Re-seed from the cart the event
            // resolved with.
            this.dataset.productId = items[items.length - 1].merchandise?.product?.id || items[items.length - 1].product_id;
            this.load();
          })
          .catch(() => {});
      };

      async load() {
        const productId = this.dataset.productId;
        if (!productId) return;

        // Cart changes can arrive faster than the endpoint answers. Stamp
        // each run so a slow earlier response cannot paint over a newer one.
        const requestId = ++this.requestId;

        // Merchandised pairings first, algorithmic ones only if there are none.
        const markup =
          (await this.fetchSection(productId, 'complementary')) ||
          (await this.fetchSection(productId, 'related'));

        if (requestId !== this.requestId) return;

        if (!markup) {
          this.innerHTML = '';
          this.hidden = true;
          return;
        }

        this.innerHTML = markup;
        this.hidden = false;
      }

      async fetchSection(productId, intent) {
        const url =
          this.base +
          // 10 is the endpoint's maximum. The section shows three, but it
          // discards the wrong gender first, so it needs more than three to
          // choose from.
          '/recommendations/products?section_id=ew_cart_upsell&limit=10&product_id=' +
          encodeURIComponent(productId) +
          '&intent=' +
          intent;

        try {
          const response = await fetch(url);
          if (!response.ok) return null;

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const root = doc.querySelector('[data-ew-upsell-root]');
          return root ? root.outerHTML : null;
        } catch (error) {
          // A missing recommendation is not worth breaking the drawer over.
          return null;
        }
      }

      onClick = async (event) => {
        const button = event.target.closest('button[data-ew-add]');
        if (!button) return;

        // Single-variant products carry the id on the button; everything
        // else reads it off the size select sitting next to it.
        const row = button.closest('.ew-upsell__item');
        const select = row && row.querySelector('[data-ew-size]');
        const variantId = Number(button.dataset.variantId || (select && select.value));
        if (!variantId) return;

        button.setAttribute('aria-busy', 'true');

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'cart-cross-sell',
            lines: [{ merchandiseId: String(variantId), quantity: 1 }],
            promise: deferred.promise,
          })
        );

        try {
          await fetch(this.base + '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
          });

          // add.js returns the line item, not the cart, and the header
          // bubble needs a total — so read the cart back before resolving.
          //
          // The read goes through update.js rather than cart.js because it
          // also has to stamp the cart. Same request count, and the response
          // is the full cart either way.
          //
          // The stamp marks the order as having gone through this row, so
          // its contribution can be measured instead of guessed. A theme
          // cannot write order tags directly, only cart attributes (which
          // become order note attributes). A Shopify Flow automation turns
          // this one into the real tag:
          //   Trigger    Order created
          //   Condition  order.customAttributes any: key = _cross_sell_cart
          //   Action     Add order tags -> cross-sell-cart
          //
          // Deliberately a different key from the PDP's `_cross_sell_pdp`
          // (blocks/ew_complete_look.liquid) so the two merchandising
          // surfaces stay measurable apart.
          const ajaxCart = await (
            await fetch(this.base + '/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
              body: JSON.stringify({ attributes: { _cross_sell_cart: 'cart-drawer' } }),
            })
          ).json();

          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: {
              items: ajaxCart.items,
              source: 'ew-cart-upsell',
              sourceId: 'ew-cart-upsell',
              itemCount: ajaxCart.item_count,
              didError: false,
            },
          });
        } catch (error) {
          button.removeAttribute('aria-busy');
          deferred.reject(error);
        }
      };
    }
  );
}
